import { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import typescript from "../../assets/images/TypeScript.svg";
import mysql from "../../assets/images/MySQL.svg";
import html from "../../assets/images/HTML5.svg";
import css from "../../assets/images/CSS3.svg";
import vercel from "../../assets/images/Vercel.svg";
import gcp from "../../assets/images/Google Cloud.svg";
import github from "../../assets/images/github.svg";
import junit from "../../assets/images/JUnit.svg";
import prisma from "../../assets/images/prisma.svg";
import react from "../../assets/images/react.svg";
import tailwind from "../../assets/images/tailwind.svg";
import vscode from "../../assets/images/Visual Studio Code (VS Code).svg";
import firebase from "../../assets/images/firebase.svg";
import openai from "../../assets/images/openAI.svg";
import gemini from "../../assets/images/Gemini.svg";
import excel from "../../assets/images/excel.svg";
import powerbi from "../../assets/images/BI.svg";
import xd from "../../assets/images/xd.svg";
import arduino from "../../assets/images/Arduino.svg";
import mongo from "../../assets/images/mongo.svg";
import postman from "../../assets/images/postman.svg";
import node from "../../assets/images/node.svg";
import figma from "../../assets/images/figma.svg";
import mui from "../../assets/images/mui.svg";
import js from "../../assets/images/js.svg";
import git from "../../assets/images/git.svg";
import cpp from "../../assets/images/cppmain.svg";
import docker from "../../assets/images/Docker.svg";
import express from "../../assets/images/Express.svg";
import postgresql from "../../assets/images/PostgresSQL.svg";
import spring from "../../assets/images/Spring.svg";
import graphql from "../../assets/images/GraphQL.svg";
import java from "../../assets/images/Java.svg";
import nextjs from "../../assets/images/Next.js.svg";
import python from "../../assets/images/Python.svg";
import socketio from "../../assets/images/Socket.io.svg";
import esp32 from "../../assets/images/esp32.png";
import esp8266 from "../../assets/images/esp8266.png";
import rfid from "../../assets/images/rfid.png";
import sensors from "../../assets/images/sensor.png";
import actuators from "../../assets/images/actuator.png";
import motor from "../../assets/images/motorcontroller.png";
import embeddedc from "../../assets/images/ec.svg";
import pcb from "../../assets/images/pcb.png";
import microcontroller from "../../assets/images/microcontroller.png";
import circuit from "../../assets/images/circuit.png";

gsap.registerPlugin(ScrollTrigger);

const TechStack = () => {
  useEffect(() => {
    // Smooth fade-in heading animation
    gsap.fromTo(
      "#techstack-heading",
      { 
        opacity: 0, 
        y: 50,
      },
      {
        opacity: 1,
        y: 0,
        duration: 1.5,
        ease: "power3.out",
        scrollTrigger: {
          trigger: "#tech-stack-section",
          start: "top 80%",
        },
      }
    );

    // Smooth fade-in for category cards
    gsap.fromTo(
      ".tech-category",
      { 
        opacity: 0, 
        y: 40,
      },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        stagger: 0.15,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".tech-categories",
          start: "top 75%",
        },
      }
    );

    // Smooth fade-in for tech items
    gsap.fromTo(
      ".tech-item",
      { 
        opacity: 0, 
        y: 20,
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.03,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".tech-categories",
          start: "top 70%",
        },
      }
    );

    // Subtle breathing animation for logos (no rotation or movement)
    gsap.to(".tech-logo", {
      scale: 1.05,
      duration: 2,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
      stagger: {
        each: 0.1,
        from: "random"
      }
    });

    // Smooth pulsing gradient bars
    gsap.to(".gradient-bar", {
      opacity: 0.8,
      duration: 2,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
      stagger: 0.3
    });

    // Stats animation with smooth fade
    gsap.fromTo(
      "#stats-footer",
      { opacity: 0, y: 20 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: "#stats-footer",
          start: "top 90%",
        },
      }
    );
  }, []);

  const techCategories = [
    {
      title: "Languages",
      gradient: "from-blue-500 via-cyan-400 to-teal-500",
      bgGradient: "from-blue-500/20 to-cyan-500/20",
      items: [
        { name: "JavaScript", logo: js },
        { name: "TypeScript", logo: typescript },
        { name: "Python", logo: python },
        { name: "Java", logo: java },
        { name: "SQL", logo: mysql },
        { name: "C++", logo: cpp },
      ],
    },
    {
      title: "Frontend",
      gradient: "from-purple-500 via-pink-500 to-rose-500",
      bgGradient: "from-purple-500/20 to-pink-500/20",
      items: [
        { name: "React", logo: react },
        { name: "Next.js", logo: nextjs },
        { name: "React Native", logo: react },
        { name: "HTML5", logo: html },
        { name: "CSS3", logo: css },
        { name: "Tailwind CSS", logo: tailwind },
      ],
    },
    {
      title: "Backend",
      gradient: "from-green-500 via-emerald-400 to-teal-500",
      bgGradient: "from-green-500/20 to-emerald-500/20",
      items: [
        { name: "Node.js", logo: node },
        { name: "Express.js", logo: express },
        { name: "Spring Boot", logo: spring },
        { name: "GraphQL", logo: graphql },
        { name: "Socket.io", logo: socketio },
      ],
    },
    {
      title: "Databases",
      gradient: "from-orange-500 via-amber-400 to-yellow-500",
      bgGradient: "from-orange-500/20 to-red-500/20",
      items: [
        { name: "MongoDB", logo: mongo },
        { name: "PostgreSQL", logo: postgresql },
        { name: "MySQL", logo: mysql },
        { name: "Firebase", logo: firebase },
      ],
    },
    {
      title: "Cloud & DevOps",
      gradient: "from-indigo-500 via-blue-500 to-cyan-500",
      bgGradient: "from-indigo-500/20 to-blue-500/20",
      items: [
        { name: "Vercel", logo: vercel },
        { name: "GCP", logo: gcp },
        { name: "Git", logo: git },
        { name: "GitHub", logo: github },
        { name: "Docker", logo: docker },
        { name: "JUnit", logo: junit },
      ],
    },
    {
      title: "Development Tools",
      gradient: "from-pink-500 via-rose-500 to-red-500",
      bgGradient: "from-pink-500/20 to-rose-500/20",
      items: [
        { name: "Prisma", logo: prisma },
        { name: "Postman", logo: postman },
        { name: "Figma", logo: figma },
        { name: "VS Code", logo: vscode },
      ],
    },
    {
      title: "APIs & Integrations",
      gradient: "from-yellow-500 via-orange-400 to-red-500",
      bgGradient: "from-yellow-500/20 to-orange-500/20",
      items: [
        { name: "Google Gemini API", logo: gemini },
        { name: "OpenAI API", logo: openai },
      ],
    },
    {
      title: "Analytics",
      gradient: "from-violet-500 via-purple-500 to-fuchsia-500",
      bgGradient: "from-violet-500/20 to-purple-500/20",
      items: [
        { name: "Excel", logo: excel },
        { name: "Power BI", logo: powerbi },
        { name: "Python Libraries", logo: python },
      ],
    },
    {
      title: "UI/UX Design",
      gradient: "from-cyan-500 via-blue-500 to-indigo-500",
      bgGradient: "from-cyan-500/20 to-blue-500/20",
      items: [
        { name: "Figma", logo: figma },
        { name: "Adobe XD", logo: xd },
      ],
    },
  {
    title: "IoT & Robotics",
    gradient: "from-lime-500 via-green-500 to-emerald-500",
    bgGradient: "from-lime-500/20 to-green-500/20",
    items: [
      { name: "Arduino", logo: arduino },
      { name: "ESP32", logo: esp32 }, // Add logo when available
      { name: "ESP8266", logo: esp8266 }, // Add logo when available
      { name: "RFID", logo: rfid }, // Add logo when available
      { name: "Sensors", logo: sensors }, // Add logo when available
      { name: "Actuators", logo: actuators }, // Add logo when available
      { name: "Motor Controllers", logo: motor }, // Add logo when available
    ],
  },
  {
    title: "Embedded Systems",
    gradient: "from-red-500 via-orange-500 to-amber-500",
    bgGradient: "from-red-500/20 to-orange-500/20",
    items: [
      { name: "Embedded C/C++", logo: embeddedc }, // Reusing C++ logo
      { name: "Microcontroller Programming", logo: microcontroller }, // Add logo when available
      { name: "Circuit Design", logo: circuit }, // Add logo when available
      { name: "PCB Design", logo: pcb }, // Add logo when available
    ],
  },
  ];

  return (
    <div
      id="tech-stack-section"
      className="w-full py-20 xl:px-44 lg:px-36 md:px-32 sm:px-28 vvs:px-12 px-10 relative overflow-hidden"
    >
      {/* Animated background particles */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-10 left-1/2 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Section Heading */}
      <h1
        id="techstack-heading"
        className="relative text-white/70 font-Glimer-Outlined font-semibold lg:mb-20 mb-16 vvs:text-[4rem] sm:text-[5.2rem] md:text-[6.4rem] lg:text-[7.5rem] xl:text-[9.2rem] 2xl:text-[10.8rem] text-[3.5rem] leading-[3.5rem] sm:leading-[5.5rem] md:leading-[6rem] lg:leading-[7rem] xl:leading-[9rem] 2xl:leading-[10rem] cursor-default"
      >
        Tech Stack
      </h1>

      {/* Tech Categories Grid */}
      <div className="tech-categories space-y-8 lg:space-y-12 relative">
        {techCategories.map((category, index) => (
          <div
            key={index}
            className="tech-category relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 lg:p-10 border border-white/10 hover:border-white/30 transition-all duration-500 hover:shadow-2xl hover:shadow-white/10 group overflow-hidden"
          >
            {/* Animated background gradient */}
            <div className={`absolute inset-0 bg-gradient-to-br ${category.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
            
            {/* Rotating border effect */}
            <div className={`absolute inset-0 bg-gradient-to-r ${category.gradient} opacity-0 group-hover:opacity-20 blur-2xl transition-all duration-500`}></div>

            {/* Category Header */}
            <div className="relative flex items-center space-x-3 mb-8">
              <div
                className={`gradient-bar h-1 w-12 rounded-full bg-gradient-to-r ${category.gradient} shadow-lg`}
              ></div>
              <h3
                className={`text-white font-Glimer-Bold text-2xl md:text-3xl lg:text-4xl bg-gradient-to-r ${category.gradient} bg-clip-text text-transparent`}
              >
                {category.title}
              </h3>
            </div>

            {/* Tech Items Grid with Logos */}
            <div className="relative grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
              {category.items.map((item, idx) => (
                <div
                  key={idx}
                  className="tech-item flex flex-col items-center justify-center p-4 md:p-6 bg-white/5 rounded-xl border border-white/10 hover:bg-white/15 hover:border-white/30 hover:scale-105 transition-all duration-300 cursor-pointer group/item relative overflow-hidden"
                >
                  {/* Shimmer effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover/item:translate-x-[100%] transition-transform duration-700"></div>
                  
                  {/* Logo or Text Badge */}
                  <div className="relative mb-3">
                    {/* Animated glow */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-r ${category.gradient} opacity-0 group-hover/item:opacity-40 blur-2xl transition-all duration-500 rounded-full scale-150`}
                    ></div>
                    
                    {item.logo ? (
                      <img
                        src={item.logo}
                        alt={item.name}
                        className="tech-logo h-12 w-12 md:h-16 md:w-16 lg:h-20 lg:w-20 object-contain relative z-10 group-hover/item:scale-110 transition-all duration-500 drop-shadow-2xl"
                      />
                    ) : (
                      <div className={`tech-logo h-12 w-12 md:h-16 md:w-16 lg:h-20 lg:w-20 rounded-lg bg-gradient-to-br ${category.gradient} flex items-center justify-center relative z-10 group-hover/item:scale-110 transition-all duration-500 shadow-2xl`}>
                        <span className="text-white font-Glimer-Bold text-xs md:text-sm">
                          {item.name.split(' ').map(word => word[0]).join('').slice(0, 2)}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Tech Name with gradient on hover */}
                  <p className={`text-white/80 font-Glimer-Medium text-xs md:text-sm text-center group-hover/item:text-white transition-all duration-300 group-hover/item:bg-gradient-to-r group-hover/item:${category.gradient} group-hover/item:bg-clip-text group-hover/item:text-transparent group-hover/item:font-bold`}>
                    {item.name}
                  </p>

                  {/* Particle effect */}
                  <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                    <div className={`absolute top-2 left-2 w-1 h-1 bg-gradient-to-r ${category.gradient} rounded-full opacity-0 group-hover/item:opacity-100 group-hover/item:animate-ping`}></div>
                    <div className={`absolute bottom-2 right-2 w-1 h-1 bg-gradient-to-r ${category.gradient} rounded-full opacity-0 group-hover/item:opacity-100 group-hover/item:animate-ping`} style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Stats Footer with animation */}
      <div id="stats-footer" className="mt-16 text-center relative">
        <div className="inline-block p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
          <p className="text-white/60 font-Glimer-Light text-lg md:text-xl lg:text-2xl">
            <span className="text-white font-Glimer-Bold bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">{techCategories.length} Categories</span> •{" "}
            <span className="text-white font-Glimer-Bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
              {techCategories.reduce((acc, cat) => acc + cat.items.length, 0)}+ Technologies
            </span> •{" "}
            <span className="text-white font-Glimer-Bold bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent">Always Learning</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TechStack;